import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import DOMPurify from 'dompurify';

import { Icons } from '@icons/icons';

import { useDelayUnmount } from '@hooks/utils/useDelayUnmount';

import { useAppDispatch } from '@store/store';

import { removeSnackbar } from '@slices/common';

import { ISnackbarProps } from '@interfaces/notifications/snackbar';

import { ESnackbarTypes } from '@enums/common';

import styles from './Snackbar.module.scss';

const { successIcon, warningIcon, errorIcon } = Icons;

const DELAY_UNMOUNT_TIMEOUT = 100;

const Snackbar = ({
	id,
	message,
	type,
}: ISnackbarProps) => {
	const [isMounted, setIsMounted] = useState(true);

	const dispatch = useAppDispatch();
	const shouldRender = useDelayUnmount(isMounted, DELAY_UNMOUNT_TIMEOUT);

	const deleteSnackbar = () => setIsMounted(!isMounted);

	const isSuccess = type === ESnackbarTypes.success;
	const isWarning = type === ESnackbarTypes.warning;
	const isError = type === ESnackbarTypes.error;
	const isInfo = type === ESnackbarTypes.info;

	const snackbarClasses = cn(styles.snackbar, {
		[styles.success]: isSuccess,
		[styles.warning]: isWarning,
		[styles.error]: isError,
		[styles.info]: isInfo,
	});

	const snackbarIcon = (isSuccess || isInfo)
		? successIcon
		: isWarning
			? warningIcon
			: errorIcon;

	const snackbarTitle = isSuccess
		? 'Success'
		: isWarning
			? 'Warning'
			: isInfo
				? undefined
				: 'Error';

	useEffect(() => {
		if (!isMounted) {
			const timeoutId = setTimeout(() => {
				dispatch(removeSnackbar({ id }));
			}, DELAY_UNMOUNT_TIMEOUT);

			return () => {
				clearTimeout(timeoutId);
			};
		}
	}, [id, isMounted, dispatch]);

	return shouldRender
		? <div
			className={cn(styles.snackbarContainer, {
				[styles.removeSnackbar]: !isMounted,
			})}
			data-testid='snackbar'
			onClick={deleteSnackbar}
		>
			<div className={snackbarClasses}>
				<div className={styles.icon}>{snackbarIcon()}</div>
				<div className={styles?.message}>
					<div className={styles.title}>
						{snackbarTitle}
					</div>
					<div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }} />
				</div>
			</div>
		</div>
		: null;
};

export default Snackbar;
